/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — node drag, Revit-style snap alignment, team-box
   group drag, and align/distribute. This is the most cross-cutting
   part of the old workspace (drag handlers read/write snap + undo +
   multiplayer state together) — kept as ONE hook rather than forced
   apart into unrelated pieces, per the People Rebuild §3 note that
   splitting this further requires a real behavior change, not a
   mechanical one. Everything it needs flows in as explicit
   parameters/context — no window globals.
   ═══════════════════════════════════════════════════════════════ */

import { useCallback, useRef, useState } from 'react';
import { useReactFlow, type Node, type OnNodeDrag } from '@xyflow/react';
import type { Team, TeamAssignment } from '../../../../../shared/types';
import type { OrgChartEdge } from '../../../../../shared/entities';
import { NODE_WIDTH, NODE_HEIGHT, type PositionUpdate } from '../types';
import type { SnapTargets } from '../context/OrgChartCanvasContext';
import type { UndoCmd } from './useUndo';

const SNAP_THRESHOLD = 8;
const SINGLE_AXIS_PENALTY = 1000;

/** Default equidistant gap for proximity snap (Josh grant 2026-08-18:
 *  "snap to a default distance away, so each team has equidistant
 *  gaps"). Matches the 4/8 spacing scale's `--space-4` (base.css). */
const DEFAULT_GAP = 16;
/** Minimum shared-axis overlap (as a fraction of the narrower card)
 *  required before two cards are considered "stacking" or
 *  "side-by-side" candidates for proximity snap. */
const PROXIMITY_OVERLAP_FRACTION = 0.3;
/** Proximity snap engages while the facing-edge gap is under this —
 *  "almost touching" — independent of the plain-alignment
 *  SNAP_THRESHOLD above. */
const PROXIMITY_ACTIVATION = 32;

/** Nested Team Containers: `teamId` plus every descendant team (via
 *  `parentTeamId`) — the "whole world" a dragged team box owns. Cycle
 *  guarded the same way TeamsPanel's descendant check is (a corrupt
 *  parent chain can never infinite-loop here); the cycle-safe parent
 *  picker in TeamsPanel should make that unreachable in practice. */
export function collectSubtreeTeamIds(teamId: string, teams: Team[]): Set<string> {
  const childrenOf = new Map<string, string[]>();
  for (const t of teams) {
    if (!t.parentTeamId) continue;
    if (!childrenOf.has(t.parentTeamId)) childrenOf.set(t.parentTeamId, []);
    childrenOf.get(t.parentTeamId)!.push(t.id);
  }
  const subtreeTeamIds = new Set<string>();
  const queue = [teamId];
  while (queue.length) {
    const id = queue.pop()!;
    if (subtreeTeamIds.has(id)) continue; // cycle guard
    subtreeTeamIds.add(id);
    for (const childId of childrenOf.get(id) || []) queue.push(childId);
  }
  return subtreeTeamIds;
}

/** `teamId`'s ancestor chain via `parentTeamId`, NOT including `teamId`
 *  itself — cycle-guarded the same way `collectSubtreeTeamIds` is.
 *  Regression fix (2026-08-18, Josh feel-test — "something with a team
 *  within a team"): a dragged team box's snap-candidate EXCLUSION set
 *  only ever covered its own subtree (self + descendants), never its
 *  ancestors. A nested child box is always geometrically inside its
 *  parent's box by construction, so with the parent left IN the
 *  candidate pool, ordinary alignment/gap-proximity scoring could pick
 *  one of the parent's own edges as a "closer" target than any real
 *  sibling — nudging the child toward/along the parent's edge instead
 *  of wherever it was actually dropped. Combined with the parent box
 *  being DERIVED (not fixed), a false snap here is what read as
 *  "snapping to a random spot." Used ONLY for snap-target exclusion
 *  below, never for `collectSubtreeMemberIds`/reparent-hover hit
 *  testing (dropping a nested child ONTO an ancestor's box to un-nest
 *  it up a level is a legitimate, unrelated gesture that must still
 *  detect that ancestor as a valid drop target). */
export function collectAncestorTeamIds(teamId: string, teams: Team[]): Set<string> {
  const teamById = new Map(teams.map((t) => [t.id, t]));
  const ancestorTeamIds = new Set<string>();
  let current = teamById.get(teamId)?.parentTeamId;
  while (current && !ancestorTeamIds.has(current)) {
    ancestorTeamIds.add(current);
    current = teamById.get(current)?.parentTeamId;
  }
  return ancestorTeamIds;
}

/** Every member card belonging to `teamId` OR any descendant team, so
 *  dragging a team box moves its whole containment subtree as one
 *  unit — the box itself never needs to move descendant BOXES
 *  explicitly, since OrgChartV2's team-box derivation effect
 *  recomputes every box (including nested ones) from current member
 *  positions on every `nodes` change. */
export function collectSubtreeMemberIds(
  teamId: string,
  teams: Team[],
  teamAssignments: TeamAssignment[]
): string[] {
  const subtreeTeamIds = collectSubtreeTeamIds(teamId, teams);
  const memberIds = new Set<string>();
  for (const a of teamAssignments) {
    if (subtreeTeamIds.has(a.teamId)) memberIds.add(a.teamMemberId);
  }
  return [...memberIds];
}

export type AlignKind = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';
export type DistributeKind = 'horizontal' | 'vertical';

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** One proximity-snap candidate: a single combined move that sets the
 *  facing-edge gap to DEFAULT_GAP and aligns the perpendicular
 *  centers, expressed in the same (target, offset-within-node) shape
 *  the plain alignment candidates use so both flow through the same
 *  `snappedX/snappedY` resolution below. */
export interface ProximityCandidate {
  xTarget: number;
  xOffset: number;
  yTarget: number;
  yOffset: number;
  axis: 'vertical' | 'horizontal';
  /** Facing-edge gap BEFORE snapping (can be negative if the cards
   *  already overlap slightly). */
  gap: number;
  /** True when the dragged card is below (vertical) / right of
   *  (horizontal) the other card — which pair of facing edges to draw
   *  the gap-indicator tick between. */
  draggedIsAfter: boolean;
}

/** Pure candidate-builder — no React/refs. Returns the tighter of a
 *  stacking (vertical) or side-by-side (horizontal) proximity
 *  candidate between `dragged` and `other`, or null if neither is
 *  close enough / overlapping enough to qualify. Exported for the
 *  candidate-math test harness. */
export function buildProximityCandidate(dragged: Rect, other: Rect): ProximityCandidate | null {
  const dLeft = dragged.x;
  const dRight = dragged.x + dragged.w;
  const dTop = dragged.y;
  const dBottom = dragged.y + dragged.h;
  const dCx = dragged.x + dragged.w / 2;
  const dCy = dragged.y + dragged.h / 2;
  const oLeft = other.x;
  const oRight = other.x + other.w;
  const oTop = other.y;
  const oBottom = other.y + other.h;
  const oCx = other.x + other.w / 2;
  const oCy = other.y + other.h / 2;

  let stack: ProximityCandidate | null = null;
  const xOverlap = Math.min(dRight, oRight) - Math.max(dLeft, oLeft);
  const narrowerW = Math.min(dragged.w, other.w);
  if (narrowerW > 0 && xOverlap / narrowerW >= PROXIMITY_OVERLAP_FRACTION) {
    const draggedIsAfter = dCy >= oCy; // dragged sits below the other card
    const gap = draggedIsAfter ? dTop - oBottom : oTop - dBottom;
    if (gap < PROXIMITY_ACTIVATION) {
      stack = {
        xTarget: oCx,
        xOffset: dragged.w / 2,
        yTarget: draggedIsAfter ? oBottom + DEFAULT_GAP : oTop - DEFAULT_GAP,
        yOffset: draggedIsAfter ? 0 : dragged.h,
        axis: 'vertical',
        gap,
        draggedIsAfter,
      };
    }
  }

  let side: ProximityCandidate | null = null;
  const yOverlap = Math.min(dBottom, oBottom) - Math.max(dTop, oTop);
  const narrowerH = Math.min(dragged.h, other.h);
  if (narrowerH > 0 && yOverlap / narrowerH >= PROXIMITY_OVERLAP_FRACTION) {
    const draggedIsAfter = dCx >= oCx; // dragged sits to the right of the other card
    const gap = draggedIsAfter ? dLeft - oRight : oLeft - dRight;
    if (gap < PROXIMITY_ACTIVATION) {
      side = {
        xTarget: draggedIsAfter ? oRight + DEFAULT_GAP : oLeft - DEFAULT_GAP,
        xOffset: draggedIsAfter ? 0 : dragged.w,
        yTarget: oCy,
        yOffset: dragged.h / 2,
        axis: 'horizontal',
        gap,
        draggedIsAfter,
      };
    }
  }

  if (stack && side) return Math.abs(stack.gap) <= Math.abs(side.gap) ? stack : side;
  return stack || side;
}

/** Live gap-indicator payload for the currently-active proximity
 *  snap, if any — read directly by `SnapGuideLines` (overlays.tsx) via
 *  module import rather than routed through the OrgChartV2 render
 *  props chain. OrgChartV2.tsx's `<SnapGuideLines hY={...} vX={...}/>`
 *  call site is outside this task's file fence (owned by a parallel
 *  agent), so it can't be edited to forward a new prop; this mutable
 *  ref is the in-fence equivalent of the existing `snapTargetsRef`
 *  bridge pattern (updated every drag frame, read at render time —
 *  not a window global, not persisted). See task report for the
 *  full note. */
export interface ProximityGapIndicator {
  axis: 'vertical' | 'horizontal';
  /** Flow-space coordinate shared by both cards after snapping — the
   *  aligned centerX (vertical gap) or centerY (horizontal gap). */
  cross: number;
  /** Flow-space start/end of the gap along its axis (the two facing
   *  edges), `to - from === DEFAULT_GAP`. */
  from: number;
  to: number;
}

export const proximitySnapGapRef: { current: ProximityGapIndicator | null } = { current: null };

function buildGapIndicator(
  draggedRect: Rect,
  otherRect: Rect,
  candidate: ProximityCandidate
): ProximityGapIndicator {
  if (candidate.axis === 'vertical') {
    const [from, to] = candidate.draggedIsAfter
      ? [otherRect.y + otherRect.h, draggedRect.y]
      : [draggedRect.y + draggedRect.h, otherRect.y];
    return { axis: 'vertical', cross: draggedRect.x + draggedRect.w / 2, from, to };
  }
  const [from, to] = candidate.draggedIsAfter
    ? [otherRect.x + otherRect.w, draggedRect.x]
    : [draggedRect.x + draggedRect.w, otherRect.x];
  return { axis: 'horizontal', cross: draggedRect.y + draggedRect.h / 2, from, to };
}

export interface UseNodeDragAndSnapDeps {
  /** The saved (pre-drag) position for a member, sourced from whichever
   *  board is currently active (People Rebuild, Josh grant 2026-08-17:
   *  "views become separate canvases/boards" — "positions source
   *  swapped"): `team_members.orgX/orgY` on Main, this board's
   *  `org_chart_view_positions` row on a custom view. Replaces the old
   *  direct `members.find(...).orgX/orgY` read, which always pointed at
   *  Main's saved position even while editing a custom board — the
   *  undo baseline for a drag would have silently reverted to the
   *  WRONG board's position. Returns undefined when there's no prior
   *  saved position (brand-new placement this session) — callers already
   *  fall back to the post-drag value in that case. */
  resolvePosition: (id: string) => { x: number; y: number } | undefined;
  teams: Team[];
  teamAssignments: TeamAssignment[];
  orgChartEdges: OrgChartEdge[];
  /** Edit-session model (People Rebuild §0/§3, 2026-08-17): this is now
   *  ALWAYS a pure local-state writer (session bookkeeping only, never
   *  network) — the caller supplies it, this hook no longer imports or
   *  calls the network batch endpoint itself. */
  onSavePositions: (updates: PositionUpdate[]) => void;
  onUpdateJunction: (id: string, patch: { x: number; y: number }) => void;
  /** Text-box note position writer (People Rebuild, Josh grant
   *  2026-08-17) — unlike junctions, note moves ARE undo-tracked (see
   *  onNodeDragStart/onNodeDragStop below), so this hook needs
   *  `pushUndo` for notes specifically even though it never used it
   *  for junction drags. */
  onUpdateNotePosition: (id: string, patch: { x: number; y: number }) => void;
  pushUndo: (cmd: UndoCmd) => void;
  broadcastDrag: (
    payload: Array<{ id: string; x: number; y: number; type: 'person' | 'junction' }>,
    active: boolean
  ) => void;
  localDraggingIds: React.MutableRefObject<Set<string>>;
  snapEnabledRef: React.MutableRefObject<boolean>;
  snapTargetsRef: React.MutableRefObject<SnapTargets>;
  /** The component's OWN controlled node-state setter (from OrgChartV2's
   *  `useNodesState`) — see "single write channel" note below. MUST be
   *  used for every position write this hook makes, live-drag frames
   *  included, never `useReactFlow().setNodes`. */
  setNodes: (updater: (nodes: Node[]) => Node[]) => void;
}

/** Regression fix (2026-08-18, urgent — Josh feel-test: team boxes
 *  "keep snapping back to a random spot", cards "not landing where I
 *  place them"): this hook used to write live drag positions straight
 *  into the React Flow STORE via `useReactFlow().setNodes` — a second
 *  channel that bypasses OrgChartV2's own controlled `nodes` state
 *  (`useNodesState`). @xyflow/react's `<ReactFlow nodes={...}>` re-syncs
 *  the store from that controlled prop by REFERENCE on every render
 *  where it changes (see `StoreUpdater` in the library — a plain
 *  `useIsomorphicLayoutEffect` keyed on `props.nodes`). OrgChartV2's
 *  "Live team-box backdrops" effect calls the component's OWN
 *  `setNodes` on nearly every drag frame (it's keyed on `dragHoverHint`,
 *  which the v2.17.10 drag-hint wrappers update every frame too) — and
 *  since THAT write only ever knew about pre-drag positions (the
 *  store-only channel never touched it), every one of those re-derives
 *  stomped the live drag back to a stale snapshot. Mid-gesture, for a
 *  recursive team-box drag, that reads as "keeps snapping back to a
 *  random spot." Fix: ONE write channel — every position write in this
 *  hook (live-frame and drop) goes through the SAME controlled
 *  `setNodes` the rest of OrgChartV2 already uses, so there is no
 *  second, invisible-to-React-state place for positions to live that a
 *  routine re-render can silently undo. `getNodes()` (reads) still
 *  comes from `useReactFlow()` — the store reflects controlled-state
 *  writes reliably now that nothing else fights it. */
export function useNodeDragAndSnap(deps: UseNodeDragAndSnapDeps) {
  const { getNodes } = useReactFlow();
  const rfSetNodes = deps.setNodes;
  const [snapLines, setSnapLines] = useState<{ hY?: number; vX?: number }>({});
  const snapActive = useRef(false);
  const snapPositions = useRef<Record<string, { x: number; y: number }>>({});
  const teamDragStart = useRef<{
    teamBoxStartPos: { x: number; y: number };
    memberStartPositions: Map<string, { x: number; y: number }>;
    /** Nested Team Containers — every team id in this box's subtree
     *  (itself + descendants), excluded as snap TARGETS so a dragged
     *  parent box never "snaps" against its own nested children, which
     *  move with it deterministically. */
    subtreeTeamIds: Set<string>;
    /** Regression fix (2026-08-18) — subtree PLUS every ancestor team,
     *  the full snap-target exclusion set. A box is always inside its
     *  parent's box by construction, so the parent (and grandparents)
     *  must never be a snap candidate either — see
     *  `collectAncestorTeamIds`'s header comment. */
    snapExcludeTeamIds: Set<string>;
    /** Regression fix (2026-08-18) — the dragged team's own
     *  `parentTeamId` (null for top-level). Snap-candidate scoring is
     *  restricted to OTHER boxes sharing this SAME value — see the
     *  construction site's comment. */
    siblingParentTeamId: string | null;
    boxW: number;
    boxH: number;
    /** Best alignment/proximity snap found during the live drag, if
     *  any — applied to the committed member positions at drop only
     *  (same two-phase pattern as card drag: raw movement while
     *  dragging, snap resolves on release). */
    snapDelta: { dx: number; dy: number } | null;
  } | null>(null);
  /** Pre-drag positions of any text-box notes in this drag gesture —
   *  the undo command needs a "prev" to diff against, and (unlike
   *  members/junctions) notes have no props-derived source of truth to
   *  fall back on mid-drag. */
  const noteDragStart = useRef<Map<string, { x: number; y: number }>>(new Map());

  const onNodeDragStart: OnNodeDrag = useCallback(
    (_event, node, allDraggedNodes) => {
      for (const n of allDraggedNodes) {
        deps.localDraggingIds.current.add(n.id);
        if (n.type === 'textBox') {
          noteDragStart.current.set(n.id, { x: n.position.x, y: n.position.y });
        }
      }
      if (node.type === 'teamBox') {
        const teamId = node.id.replace(/^team-box-/, '');
        const subtreeTeamIds = collectSubtreeTeamIds(teamId, deps.teams);
        const memberIds = collectSubtreeMemberIds(teamId, deps.teams, deps.teamAssignments);
        const startPositions = new Map<string, { x: number; y: number }>();
        for (const n of getNodes()) {
          if (n.type === 'person' && memberIds.includes(n.id)) {
            startPositions.set(n.id, { x: n.position.x, y: n.position.y });
            deps.localDraggingIds.current.add(n.id);
          }
        }
        const ancestorTeamIds = collectAncestorTeamIds(teamId, deps.teams);
        const dd = (node.data || {}) as { width?: number; height?: number };
        teamDragStart.current = {
          teamBoxStartPos: { x: node.position.x, y: node.position.y },
          memberStartPositions: startPositions,
          subtreeTeamIds,
          snapExcludeTeamIds: new Set([...subtreeTeamIds, ...ancestorTeamIds]),
          // Same-containment-level restriction (spec — "only against
          // SIBLING boxes at the same containment level"): the dragged
          // team's OWN parentTeamId (null for top-level). A box can
          // ONLY snap-align against another box that shares this same
          // parent — never a box from a totally unrelated branch of
          // the chart that just happens to be geometrically close at
          // some frame while the drag passes through/near it. Chart-
          // wide "nest under X" hover detection (OrgChartV2's
          // findContainingTeamBoxId) is a SEPARATE, intentionally
          // chart-wide feature and is untouched by this.
          siblingParentTeamId: deps.teams.find((t) => t.id === teamId)?.parentTeamId ?? null,
          boxW: node.measured?.width ?? dd.width ?? 0,
          boxH: node.measured?.height ?? dd.height ?? 0,
          snapDelta: null,
        };
      }
    },
    [deps.teamAssignments, deps.teams, deps.localDraggingIds, getNodes]
  );

  const onNodeDrag: OnNodeDrag = useCallback(
    (_event, draggedNode, allDraggedNodes) => {
      const groupDrag = teamDragStart.current;
      if (groupDrag && draggedNode.type === 'teamBox') {
        // Recursive drag: the box itself keeps tracking the RAW mouse
        // delta while dragging (same feel as a card drag) — the
        // subtree's member cards ride along 1:1. Nested Team
        // Containers' box outlines are all DERIVED from member
        // positions (OrgChartV2's team-box effect), so moving the
        // members is the whole mechanism; no separate box-node move
        // is needed here.
        const dx = draggedNode.position.x - groupDrag.teamBoxStartPos.x;
        const dy = draggedNode.position.y - groupDrag.teamBoxStartPos.y;
        rfSetNodes((nds) =>
          nds.map((n) => {
            const start = groupDrag.memberStartPositions.get(n.id);
            if (!start) return n;
            return { ...n, position: { x: start.x + dx, y: start.y + dy } };
          })
        );
        const payload: Array<{ id: string; x: number; y: number; type: 'person' }> = [];
        for (const [id, start] of groupDrag.memberStartPositions.entries()) {
          payload.push({ id, x: start.x + dx, y: start.y + dy, type: 'person' });
        }
        deps.broadcastDrag(payload, true);
        // No gap-tick overlay for box-vs-box proximity (card-to-card
        // only, per useNodeDragAndSnap's existing scope) — clear any
        // stale indicator left over from a prior card drag.
        proximitySnapGapRef.current = null;

        // Team boxes join the SAME alignment guides + 16px proximity
        // gap snap as member cards (spec §3) — scored against every
        // OTHER top-level/sibling team box, excluding this box's own
        // subtree (which moves with it deterministically and would
        // only ever produce a false-positive self-snap). Guides only
        // during the live drag; the resolved delta is stashed on
        // `groupDrag.snapDelta` and actually applied to the committed
        // member positions at drop (onNodeDragStop) — the same
        // two-phase pattern as the plain card-drag branch below.
        if (!deps.snapEnabledRef.current) {
          groupDrag.snapDelta = null;
          setSnapLines({});
          return;
        }
        const w = groupDrag.boxW;
        const h = groupDrag.boxH;
        const dL = groupDrag.teamBoxStartPos.x + dx;
        const dT = groupDrag.teamBoxStartPos.y + dy;
        const draggedRect: Rect = { x: dL, y: dT, w, h };
        const xCandsHere: Array<[number, number]> = [
          [dL, 0],
          [dL + w / 2, w / 2],
          [dL + w, w],
        ];
        const yCandsHere: Array<[number, number]> = [
          [dT, 0],
          [dT + h / 2, h / 2],
          [dT + h, h],
        ];
        let bxDist = SNAP_THRESHOLD;
        let bxTarget: number | undefined;
        let bxOffset = 0;
        let byDist = SNAP_THRESHOLD;
        let byTarget: number | undefined;
        let byOffset = 0;
        let bestProximity: { candidate: ProximityCandidate; moveDist: number } | null = null;
        for (const n of getNodes()) {
          if (n.type !== 'teamBox' || n.id === draggedNode.id) continue;
          const otherTeamId = n.id.replace(/^team-box-/, '');
          if (groupDrag.snapExcludeTeamIds.has(otherTeamId)) continue;
          // Same-containment-level restriction — never snap against a
          // box from an unrelated branch of the chart just because the
          // drag happens to pass near it.
          const otherTeam = deps.teams.find((t) => t.id === otherTeamId);
          if ((otherTeam?.parentTeamId ?? null) !== groupDrag.siblingParentTeamId) continue;
          const ndd = (n.data || {}) as { width?: number; height?: number };
          const nw = n.measured?.width ?? ndd.width ?? 0;
          const nh = n.measured?.height ?? ndd.height ?? 0;
          for (const [cand, off] of xCandsHere) {
            for (const t of [n.position.x, n.position.x + nw / 2, n.position.x + nw]) {
              const d = Math.abs(cand - t);
              if (d < bxDist) {
                bxDist = d;
                bxTarget = t;
                bxOffset = off;
              }
            }
          }
          for (const [cand, off] of yCandsHere) {
            for (const t of [n.position.y, n.position.y + nh / 2, n.position.y + nh]) {
              const d = Math.abs(cand - t);
              if (d < byDist) {
                byDist = d;
                byTarget = t;
                byOffset = off;
              }
            }
          }
          const otherRect: Rect = { x: n.position.x, y: n.position.y, w: nw, h: nh };
          const candidate = buildProximityCandidate(draggedRect, otherRect);
          if (candidate) {
            const moveDist =
              Math.abs(candidate.xTarget - candidate.xOffset - draggedRect.x) +
              Math.abs(candidate.yTarget - candidate.yOffset - draggedRect.y);
            if (!bestProximity || moveDist < bestProximity.moveDist) {
              bestProximity = { candidate, moveDist };
            }
          }
        }
        if (bestProximity) {
          const snappedX = bestProximity.candidate.xTarget - bestProximity.candidate.xOffset;
          const snappedY = bestProximity.candidate.yTarget - bestProximity.candidate.yOffset;
          groupDrag.snapDelta = {
            dx: snappedX - groupDrag.teamBoxStartPos.x,
            dy: snappedY - groupDrag.teamBoxStartPos.y,
          };
          setSnapLines({
            hY: bestProximity.candidate.yTarget,
            vX: bestProximity.candidate.xTarget,
          });
        } else if (bxTarget !== undefined || byTarget !== undefined) {
          const snappedX = bxTarget !== undefined ? bxTarget - bxOffset : dL;
          const snappedY = byTarget !== undefined ? byTarget - byOffset : dT;
          groupDrag.snapDelta = {
            dx: snappedX - groupDrag.teamBoxStartPos.x,
            dy: snappedY - groupDrag.teamBoxStartPos.y,
          };
          setSnapLines({ hY: byTarget, vX: bxTarget });
        } else {
          groupDrag.snapDelta = null;
          setSnapLines({});
        }
        return;
      }

      const payload = allDraggedNodes.map((n) => {
        const sp = snapPositions.current[n.id];
        return {
          id: n.id,
          x: sp?.x ?? n.position.x,
          y: sp?.y ?? n.position.y,
          type: (n.type === 'junction' ? 'junction' : 'person') as 'person' | 'junction',
        };
      });
      deps.broadcastDrag(payload, true);

      if (!deps.snapEnabledRef.current) {
        snapActive.current = false;
        setSnapLines({});
        proximitySnapGapRef.current = null;
        delete snapPositions.current[draggedNode.id];
        return;
      }
      const allNodes = getNodes();
      const w = draggedNode.measured?.width ?? NODE_WIDTH;
      const h = draggedNode.measured?.height ?? NODE_HEIGHT;
      const dL = draggedNode.position.x;
      const dCx = draggedNode.position.x + w / 2;
      const dR = draggedNode.position.x + w;
      const dT = draggedNode.position.y;
      const dCy = draggedNode.position.y + h / 2;
      const dB = draggedNode.position.y + h;
      const xCands: Array<[number, number]> = [
        [dL, 0],
        [dCx, w / 2],
        [dR, w],
      ];
      const yCands: Array<[number, number]> = [
        [dT, 0],
        [dCy, h / 2],
        [dB, h],
      ];

      type BestSnap = {
        xTarget?: number;
        xOffset: number;
        yTarget?: number;
        yOffset: number;
        score: number;
      };
      let best: BestSnap | null = null;

      // Proximity snap (Josh grant 2026-08-18) — a higher-priority,
      // dual-axis candidate layered on top of the plain alignment
      // pass above: when the dragged card is almost touching another,
      // propose ONE move that both aligns the perpendicular centers
      // AND sets the facing-edge gap to DEFAULT_GAP. Tracked
      // separately from `best`/`considerTarget` (not folded into that
      // scoring) so it always wins when present — simpler and more
      // predictable than blending scores, and keeps the existing
      // alignment scoring untouched per §"extend candidates, don't
      // rewrite".
      let bestProximity: {
        candidate: ProximityCandidate;
        otherRect: Rect;
        moveDist: number;
      } | null = null;
      const draggedRect: Rect = { x: dL, y: dT, w, h };

      function considerTarget(targetX: number[], targetY: number[]): void {
        let bxDist = SNAP_THRESHOLD;
        let bxTarget: number | undefined;
        let bxOffset = 0;
        for (const [cand, off] of xCands) {
          for (const t of targetX) {
            const d = Math.abs(cand - t);
            if (d < bxDist) {
              bxDist = d;
              bxTarget = t;
              bxOffset = off;
            }
          }
        }
        let byDist = SNAP_THRESHOLD;
        let byTarget: number | undefined;
        let byOffset = 0;
        for (const [cand, off] of yCands) {
          for (const t of targetY) {
            const d = Math.abs(cand - t);
            if (d < byDist) {
              byDist = d;
              byTarget = t;
              byOffset = off;
            }
          }
        }
        if (bxTarget === undefined && byTarget === undefined) return;
        const score =
          bxTarget !== undefined && byTarget !== undefined
            ? bxDist + byDist
            : (bxTarget !== undefined ? bxDist : byDist) + SINGLE_AXIS_PENALTY;
        if (!best || score < best.score) {
          best = {
            xTarget: bxTarget,
            xOffset: bxOffset,
            yTarget: byTarget,
            yOffset: byOffset,
            score,
          };
        }
      }

      for (const n of allNodes) {
        if (n.id === draggedNode.id) continue;
        if (n.type !== 'person') continue;
        const nw = n.measured?.width ?? NODE_WIDTH;
        const nh = n.measured?.height ?? NODE_HEIGHT;
        considerTarget(
          [n.position.x, n.position.x + nw / 2, n.position.x + nw],
          [n.position.y, n.position.y + nh / 2, n.position.y + nh]
        );
        // Proximity only applies card-to-card (dragging a person card
        // near another person card) — same single pass, no extra
        // node-list traversal.
        if (draggedNode.type === 'person') {
          const otherRect: Rect = { x: n.position.x, y: n.position.y, w: nw, h: nh };
          const candidate = buildProximityCandidate(draggedRect, otherRect);
          if (candidate) {
            const moveDist =
              Math.abs(candidate.xTarget - candidate.xOffset - draggedRect.x) +
              Math.abs(candidate.yTarget - candidate.yOffset - draggedRect.y);
            if (!bestProximity || moveDist < bestProximity.moveDist) {
              bestProximity = { candidate, otherRect, moveDist };
            }
          }
        }
      }
      for (const e of deps.orgChartEdges) {
        for (const wp of e.waypoints || []) {
          considerTarget([wp.x], [wp.y]);
        }
      }
      // Also snap against the context-shared target list (feeds edge
      // corner-drag snapping the same target set).
      deps.snapTargetsRef.current = {
        xs: allNodes
          .filter((n) => n.type === 'person')
          .flatMap((n) => {
            const nw = n.measured?.width ?? NODE_WIDTH;
            return [n.position.x, n.position.x + nw / 2, n.position.x + nw];
          }),
        ys: allNodes
          .filter((n) => n.type === 'person')
          .flatMap((n) => {
            const nh = n.measured?.height ?? NODE_HEIGHT;
            return [n.position.y, n.position.y + nh / 2, n.position.y + nh];
          }),
      };

      // Proximity always wins when present — it's dual-axis by
      // construction (an alignment + a spacing decision made
      // together), which is the whole point of "almost touching"
      // (spec §5).
      const bestSnap: BestSnap | null = bestProximity
        ? {
            xTarget: bestProximity.candidate.xTarget,
            xOffset: bestProximity.candidate.xOffset,
            yTarget: bestProximity.candidate.yTarget,
            yOffset: bestProximity.candidate.yOffset,
            score: 0,
          }
        : (best as BestSnap | null);
      if (!bestSnap) {
        snapActive.current = false;
        setSnapLines({});
        proximitySnapGapRef.current = null;
        delete snapPositions.current[draggedNode.id];
        return;
      }

      const snappedX =
        bestSnap.xTarget !== undefined
          ? bestSnap.xTarget - bestSnap.xOffset
          : draggedNode.position.x;
      const snappedY =
        bestSnap.yTarget !== undefined
          ? bestSnap.yTarget - bestSnap.yOffset
          : draggedNode.position.y;
      snapActive.current = true;
      setSnapLines({ hY: bestSnap.yTarget, vX: bestSnap.xTarget });
      snapPositions.current[draggedNode.id] = { x: snappedX, y: snappedY };
      proximitySnapGapRef.current = bestProximity
        ? buildGapIndicator(
            { x: snappedX, y: snappedY, w, h },
            bestProximity.otherRect,
            bestProximity.candidate
          )
        : null;
    },
    [deps, getNodes, rfSetNodes]
  );

  const onNodeDragStop: OnNodeDrag = useCallback(
    (_event, node, draggedNodes) => {
      const groupStop = teamDragStart.current;
      if (groupStop && node.type === 'teamBox') {
        const rawDx = node.position.x - groupStop.teamBoxStartPos.x;
        const rawDy = node.position.y - groupStop.teamBoxStartPos.y;
        // Resolve to the best alignment/proximity snap found during
        // the drag, if any (spec §3) — same two-phase pattern as the
        // plain card-drag branch: raw movement while dragging, the
        // snap itself lands only at drop.
        const { dx, dy } = groupStop.snapDelta ?? { dx: rawDx, dy: rawDy };
        const memberUpdates: PositionUpdate[] = [];
        for (const [id, start] of groupStop.memberStartPositions.entries()) {
          memberUpdates.push({ id, orgX: start.x + dx, orgY: start.y + dy });
        }
        if (dx !== rawDx || dy !== rawDy) {
          rfSetNodes((nds) =>
            nds.map((n) => {
              const start = groupStop.memberStartPositions.get(n.id);
              return start ? { ...n, position: { x: start.x + dx, y: start.y + dy } } : n;
            })
          );
        }
        setSnapLines({});
        if (memberUpdates.length > 0) {
          deps.pushUndo({
            kind: 'move',
            updates: memberUpdates.map((u) => {
              const prev = deps.resolvePosition(u.id);
              return {
                id: u.id,
                prevX: prev?.x ?? u.orgX,
                prevY: prev?.y ?? u.orgY,
                newX: u.orgX,
                newY: u.orgY,
              };
            }),
          });
          deps.onSavePositions(memberUpdates);
        }
        deps.broadcastDrag(
          memberUpdates.map((u) => ({ id: u.id, x: u.orgX, y: u.orgY, type: 'person' as const })),
          false
        );
        for (const id of groupStop.memberStartPositions.keys())
          deps.localDraggingIds.current.delete(id);
        deps.localDraggingIds.current.delete(node.id);
        teamDragStart.current = null;
        return;
      }

      const shouldSnap = snapActive.current;
      snapActive.current = false;
      setSnapLines({});
      proximitySnapGapRef.current = null;

      if (shouldSnap) {
        rfSetNodes((nds) =>
          nds.map((n) => {
            const snapped = snapPositions.current[n.id];
            return snapped ? { ...n, position: snapped } : n;
          })
        );
      }

      const memberUpdates: PositionUpdate[] = [];
      const junctionUpdates: { id: string; x: number; y: number }[] = [];
      const noteUpdates: { id: string; x: number; y: number }[] = [];
      for (const n of draggedNodes) {
        const snapped = shouldSnap ? snapPositions.current[n.id] : undefined;
        const pos = snapped || { x: n.position.x, y: n.position.y };
        if (n.type === 'junction') {
          junctionUpdates.push({ id: n.id.replace(/^junction-/, ''), x: pos.x, y: pos.y });
        } else if (n.type === 'textBox') {
          noteUpdates.push({ id: n.id.replace(/^note-/, ''), x: pos.x, y: pos.y });
        } else {
          memberUpdates.push({ id: n.id, orgX: pos.x, orgY: pos.y });
        }
      }

      deps.broadcastDrag(
        draggedNodes
          .filter((n) => n.type !== 'textBox')
          .map((n) => {
            const snapped = shouldSnap ? snapPositions.current[n.id] : undefined;
            const pos = snapped || { x: n.position.x, y: n.position.y };
            return {
              id: n.id,
              x: pos.x,
              y: pos.y,
              type: (n.type === 'junction' ? 'junction' : 'person') as 'person' | 'junction',
            };
          }),
        false
      );
      for (const n of draggedNodes) deps.localDraggingIds.current.delete(n.id);

      for (const u of noteUpdates) {
        const start = noteDragStart.current.get(`note-${u.id}`);
        noteDragStart.current.delete(`note-${u.id}`);
        if (start && (start.x !== u.x || start.y !== u.y)) {
          deps.pushUndo({
            kind: 'note-patch',
            noteId: u.id,
            prev: { x: start.x, y: start.y },
            next: { x: u.x, y: u.y },
          });
        }
        deps.onUpdateNotePosition(u.id, { x: u.x, y: u.y });
      }

      snapPositions.current = {};
      if (memberUpdates.length > 0) {
        deps.pushUndo({
          kind: 'move',
          updates: memberUpdates.map((u) => {
            const prev = deps.resolvePosition(u.id);
            return {
              id: u.id,
              prevX: prev?.x ?? u.orgX,
              prevY: prev?.y ?? u.orgY,
              newX: u.orgX,
              newY: u.orgY,
            };
          }),
        });
        deps.onSavePositions(memberUpdates);
      }
      for (const u of junctionUpdates) deps.onUpdateJunction(u.id, { x: u.x, y: u.y });
    },
    [deps, rfSetNodes]
  );

  const commitAlignedPositions = useCallback(
    (positions: Array<{ id: string; x: number; y: number }>) => {
      if (positions.length === 0) return;
      rfSetNodes((nds) =>
        nds.map((n) => {
          const p = positions.find((pp) => pp.id === n.id);
          return p ? { ...n, position: { x: p.x, y: p.y } } : n;
        })
      );
      const memberUpdates = positions.map((p) => ({ id: p.id, orgX: p.x, orgY: p.y }));
      deps.pushUndo({
        kind: 'move',
        updates: memberUpdates.map((u) => {
          const prev = deps.resolvePosition(u.id);
          return {
            id: u.id,
            prevX: prev?.x ?? u.orgX,
            prevY: prev?.y ?? u.orgY,
            newX: u.orgX,
            newY: u.orgY,
          };
        }),
      });
      deps.onSavePositions(memberUpdates);
      deps.broadcastDrag(
        positions.map((p) => ({ id: p.id, x: p.x, y: p.y, type: 'person' as const })),
        false
      );
    },
    [deps, rfSetNodes]
  );

  const alignSelection = useCallback(
    (ids: string[], kind: AlignKind) => {
      const sel = getNodes().filter((n) => ids.includes(n.id) && n.type === 'person');
      if (sel.length < 2) return;
      const dims = sel.map((n) => ({
        n,
        w: n.measured?.width ?? NODE_WIDTH,
        h: n.measured?.height ?? NODE_HEIGHT,
      }));
      let positions: Array<{ id: string; x: number; y: number }> = [];
      switch (kind) {
        case 'left': {
          const minX = Math.min(...dims.map((d) => d.n.position.x));
          positions = dims.map((d) => ({ id: d.n.id, x: minX, y: d.n.position.y }));
          break;
        }
        case 'right': {
          const maxRight = Math.max(...dims.map((d) => d.n.position.x + d.w));
          positions = dims.map((d) => ({ id: d.n.id, x: maxRight - d.w, y: d.n.position.y }));
          break;
        }
        case 'hcenter': {
          const avg = dims.reduce((s, d) => s + d.n.position.x + d.w / 2, 0) / dims.length;
          positions = dims.map((d) => ({ id: d.n.id, x: avg - d.w / 2, y: d.n.position.y }));
          break;
        }
        case 'top': {
          const minY = Math.min(...dims.map((d) => d.n.position.y));
          positions = dims.map((d) => ({ id: d.n.id, x: d.n.position.x, y: minY }));
          break;
        }
        case 'bottom': {
          const maxBottom = Math.max(...dims.map((d) => d.n.position.y + d.h));
          positions = dims.map((d) => ({ id: d.n.id, x: d.n.position.x, y: maxBottom - d.h }));
          break;
        }
        case 'vcenter': {
          const avg = dims.reduce((s, d) => s + d.n.position.y + d.h / 2, 0) / dims.length;
          positions = dims.map((d) => ({ id: d.n.id, x: d.n.position.x, y: avg - d.h / 2 }));
          break;
        }
      }
      commitAlignedPositions(positions);
    },
    [getNodes, commitAlignedPositions]
  );

  const distributeSelection = useCallback(
    (ids: string[], kind: DistributeKind) => {
      const sel = getNodes().filter((n) => ids.includes(n.id) && n.type === 'person');
      if (sel.length < 3) return;
      const dims = sel.map((n) => ({
        n,
        w: n.measured?.width ?? NODE_WIDTH,
        h: n.measured?.height ?? NODE_HEIGHT,
      }));
      let positions: Array<{ id: string; x: number; y: number }> = [];
      if (kind === 'horizontal') {
        const sorted = [...dims].sort((a, b) => a.n.position.x - b.n.position.x);
        const firstLeft = sorted[0].n.position.x;
        const last = sorted[sorted.length - 1];
        const lastRight = last.n.position.x + last.w;
        const totalW = sorted.reduce((s, d) => s + d.w, 0);
        const gap = (lastRight - firstLeft - totalW) / (sorted.length - 1);
        let cursor = firstLeft;
        for (const d of sorted) {
          positions.push({ id: d.n.id, x: cursor, y: d.n.position.y });
          cursor += d.w + gap;
        }
      } else {
        const sorted = [...dims].sort((a, b) => a.n.position.y - b.n.position.y);
        const firstTop = sorted[0].n.position.y;
        const last = sorted[sorted.length - 1];
        const lastBottom = last.n.position.y + last.h;
        const totalH = sorted.reduce((s, d) => s + d.h, 0);
        const gap = (lastBottom - firstTop - totalH) / (sorted.length - 1);
        let cursor = firstTop;
        for (const d of sorted) {
          positions.push({ id: d.n.id, x: d.n.position.x, y: cursor });
          cursor += d.h + gap;
        }
      }
      commitAlignedPositions(positions);
    },
    [getNodes, commitAlignedPositions]
  );

  return {
    snapLines,
    onNodeDragStart,
    onNodeDrag,
    onNodeDragStop,
    alignSelection,
    distributeSelection,
    getNodes,
    rfSetNodes,
  };
}

export type { Node };
